import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabaseClient';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGES = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Hook for managing image uploads to Supabase Storage
 * Used in snippet creation and editing
 */
export function useImageUpload() {
  // Store pending images with their local blob URLs for preview
  const [pendingImages, setPendingImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Validate a file before adding
   * @param {File} file
   * @returns {{ valid: boolean, error?: string }}
   */
  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: `Invalid file type: ${file.type}. Allowed: JPEG, PNG, GIF, WebP` };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Max: 10MB` };
    }
    return { valid: true };
  };

  /**
   * Add images to pending list with validation
   * Reads file content immediately to prevent ERR_UPLOAD_FILE_CHANGED
   * (clipboard/pasted images can become stale before upload)
   * @param {FileList | File[]} files
   */
  const addImages = useCallback(async (files) => {
    setError(null);
    const fileArray = Array.from(files);

    // Check max images limit
    const remainingSlots = MAX_IMAGES - pendingImages.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToAdd = fileArray.slice(0, remainingSlots);
    if (fileArray.length > remainingSlots) {
      setError(`Only added ${remainingSlots} images. Maximum ${MAX_IMAGES} allowed.`);
    }

    const validImages = [];
    for (const file of filesToAdd) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(validation.error);
        continue;
      }

      try {
        // Read file content immediately to prevent stale reference
        // This fixes ERR_UPLOAD_FILE_CHANGED for clipboard/pasted images
        const arrayBuffer = await file.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: file.type });

        // Create blob URL for preview
        const previewUrl = URL.createObjectURL(blob);
        validImages.push({
          id: uuidv4(),
          blob,  // Store stable blob instead of file reference
          previewUrl,
          name: file.name,
          type: file.type,
        });
      } catch (err) {
        console.error('[useImageUpload] Failed to read file:', file.name, err);
        setError(`Failed to read ${file.name}`);
      }
    }

    if (validImages.length > 0) {
      setPendingImages(prev => [...prev, ...validImages]);
    }
  }, [pendingImages.length]);

  /**
   * Remove an image from pending list
   * @param {string} imageId
   */
  const removeImage = useCallback((imageId) => {
    setPendingImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image) {
        // Revoke the blob URL to free memory
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  /**
   * Clear all pending images
   */
  const clearImages = useCallback(() => {
    // Revoke all blob URLs
    pendingImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    setPendingImages([]);
    setError(null);
  }, [pendingImages]);

  /**
   * Upload all pending images to Supabase Storage
   * @param {string} userId
   * @param {string} snippetId
   * @returns {Promise<string[]>} Array of public URLs
   */
  const uploadAll = useCallback(async (userId, snippetId) => {
    if (pendingImages.length === 0) {
      return [];
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadedUrls = [];

      for (const image of pendingImages) {
        // Generate unique filename with extension
        const ext = image.name.split('.').pop() || 'jpg';
        const filename = `${uuidv4()}.${ext}`;
        const filePath = `${userId}/${snippetId}/${filename}`;

        const { data, error: uploadError } = await supabase.storage
          .from('snippet-images')
          .upload(filePath, image.blob, {
            cacheControl: '3600',
            upsert: false,
            contentType: image.type,  // Explicitly set content type from stored type
          });

        if (uploadError) {
          throw new Error(`Failed to upload ${image.name}: ${uploadError.message}`);
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('snippet-images')
          .getPublicUrl(data.path);

        uploadedUrls.push(urlData.publicUrl);

        // Revoke blob URL after successful upload
        URL.revokeObjectURL(image.previewUrl);
      }

      setPendingImages([]);
      return uploadedUrls;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, [pendingImages]);

  /**
   * Upload new images (for edit mode) and return combined array
   * @param {string} userId
   * @param {string} snippetId
   * @param {string[]} existingUrls - URLs that are already uploaded
   * @returns {Promise<string[]>} Combined array of all image URLs
   */
  const uploadNewImages = useCallback(async (userId, snippetId, existingUrls = []) => {
    const newUrls = await uploadAll(userId, snippetId);
    return [...existingUrls, ...newUrls];
  }, [uploadAll]);

  return {
    pendingImages,
    isUploading,
    error,
    addImages,
    removeImage,
    clearImages,
    uploadAll,
    uploadNewImages,
    hasImages: pendingImages.length > 0,
    canAddMore: pendingImages.length < MAX_IMAGES,
    remainingSlots: MAX_IMAGES - pendingImages.length,
  };
}

export { MAX_FILE_SIZE, MAX_IMAGES, ALLOWED_TYPES };
